import mlClient from "@/ml-client";
import {
  CapperProfileModel,
  UpdateCapperProfile,
  CreateCapperReview,
  CapperReviewModel,
  faqSchema,
  bigWinnersSchema,
  updateCapperProfileSchema,
  createCapperReviewSchema,
  MediaPost,
} from "common";
import { isValidVideoUrl } from "./video";

// Capper Profile API Functions

export async function getCapperProfile(
  handle: string,
  token?: string
): Promise<CapperProfileModel | null> {
  console.log("===GET CAPPER PROFILE===");
  const result = await mlClient.get<CapperProfileModel>(
    `/cappers/${handle}`,
    token
  );
  console.log("getCapperProfile result:", result);
  if (result.ok) {
    console.log("capper plans", result.data?.plans);
  }

  if (!result.ok) {
    console.error("Failed to fetch capper profile:", result.message);
    return null;
  }

  return result.data || null;
}

export async function getCapperProfileByClerkId(
  token: string
): Promise<CapperProfileModel | null> {
  console.log("===GET CAPPER PROFILE BY CLERK ID===");
  const result = await mlClient.get<CapperProfileModel>(`/cappers/self`, token);
  console.log("getCapperProfileByClerkId result:", result);

  if (result.ok && result.data) {
    console.log(
      "Capper profile plans from selling-subs page:",
      result.data.plans
    );

    // Log discord_channels details for each plan
    if (result.data.plans && Array.isArray(result.data.plans)) {
      result.data.plans.forEach((plan: any, index: number) => {
        console.log(
          `Plan ${index + 1} (${plan.name}) discord_channels:`,
          plan.discord_channels
        );
      });
    }
  }

  if (!result.ok) {
    console.error(
      "Failed to fetch capper profile by Clerk ID:",
      result.message
    );
    return null;
  }

  return result.data || null;
}

export async function createCapperProfile(
  token: string
): Promise<CapperProfileModel | null> {
  const result = await mlClient.post<CapperProfileModel>("/cappers", {}, token);

  if (!result.ok) {
    console.error("Failed to create capper profile:", result.message);
    return null;
  }

  return result.data || null;
}

export async function updateCapperProfile(
  profileData: UpdateCapperProfile,
  token: string
): Promise<boolean> {
  const result = await mlClient.put<void>("/cappers", profileData, token);

  if (!result.ok) {
    console.error("Failed to update capper profile:", result.message);
    return false;
  }

  return true;
}

export async function deleteCapperProfile(token: string): Promise<boolean> {
  const result = await mlClient.delete<void>("/cappers", token);

  if (!result.ok) {
    console.error("Failed to delete capper profile:", result.message);
    return false;
  }

  return true;
}

export async function uploadCapperAvatar(
  file: File,
  token: string
): Promise<string | null> {
  try {
    const presignResult = await mlClient.post<MediaPost>(
      "/media/presign",
      {
        type: "avatar",
        contentType: file.type,
      },
      token
    );

    if (!presignResult.ok || !presignResult.data) {
      console.error("Failed to get presigned URL:", presignResult.message);
      return null;
    }

    const { url, fields, public_id } = presignResult.data as any;

    const key = fields.key as string;
    const extension = key.split(".").pop() || "jpg";

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    console.log("Uploading to S3:", {
      url,
      fields,
      fileSize: file.size,
      fileType: file.type,
    });

    const uploadResponse = await fetch(url, {
      method: "POST",
      body: formData,
    });

    console.log("S3 upload response:", {
      status: uploadResponse.status,
      statusText: uploadResponse.statusText,
      ok: uploadResponse.ok,
      headers: Object.fromEntries(uploadResponse.headers.entries()),
    });

    if (uploadResponse.ok) {
      console.log("S3 upload successful, returning public_id with extension");
      return `${public_id}.${extension}`;
    } else {
      console.error("Avatar upload failed:", {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
      });
      return null;
    }
  } catch (error) {
    console.error("Error uploading avatar:", error);
    return null;
  }
}

export async function uploadCapperBigWinner(
  file: File,
  token: string
): Promise<string | null> {
  try {
    const presignResult = await mlClient.post<MediaPost>(
      "/media/presign",
      {
        type: "big_winner",
        contentType: file.type,
      },
      token
    );

    if (!presignResult.ok || !presignResult.data) {
      console.error("Failed to get presigned URL:", presignResult.message);
      return null;
    }

    const { url, fields, public_id } = presignResult.data as any;

    const key = fields.key as string;
    const extension = key.split(".").pop() || "jpg";

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    console.log("Uploading big winner to S3:", {
      url,
      fields,
      fileSize: file.size,
      fileType: file.type,
    });

    const uploadResponse = await fetch(url, {
      method: "POST",
      body: formData,
    });

    console.log("S3 big winner upload response:", {
      status: uploadResponse.status,
      statusText: uploadResponse.statusText,
      ok: uploadResponse.ok,
      headers: Object.fromEntries(uploadResponse.headers.entries()),
    });

    if (uploadResponse.ok) {
      console.log(
        "S3 big winner upload successful, returning public_id with extension"
      );
      return `${public_id}.${extension}`;
    } else {
      console.error("Big winner upload failed:", {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
      });
      return null;
    }
  } catch (error) {
    console.error("Error uploading big winner image:", error);
    return null;
  }
}

export async function uploadCapperCover(
  file: File,
  token: string
): Promise<string | null> {
  try {
    const presignResult = await mlClient.post<MediaPost>(
      "/media/presign",
      {
        type: "cover_image",
        contentType: file.type,
      },
      token
    );

    if (!presignResult.ok || !presignResult.data) {
      console.error("Failed to get presigned URL:", presignResult.message);
      return null;
    }

    const { url, fields, public_id } = presignResult.data as any;

    const key = fields.key as string;
    const extension = key.split(".").pop() || "jpg";

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    formData.append("file", file);

    console.log("Uploading cover to S3:", {
      url,
      fields,
      fileSize: file.size,
      fileType: file.type,
    });

    const uploadResponse = await fetch(url, {
      method: "POST",
      body: formData,
    });

    console.log("S3 cover upload response:", {
      status: uploadResponse.status,
      statusText: uploadResponse.statusText,
      ok: uploadResponse.ok,
      headers: Object.fromEntries(uploadResponse.headers.entries()),
    });

    if (uploadResponse.ok) {
      console.log(
        "S3 cover upload successful, returning public_id with extension"
      );
      return `${public_id}.${extension}`;
    } else {
      console.error("Cover upload failed:", {
        status: uploadResponse.status,
        statusText: uploadResponse.statusText,
      });
      return null;
    }
  } catch (error) {
    console.error("Error uploading cover image:", error);
    return null;
  }
}

export async function isCapperHandleAvailable(
  handle: string
): Promise<boolean> {
  try {
    const profile = await getCapperProfile(handle);
    return profile === null;
  } catch (error) {
    console.error("Error checking handle availability:", error);
    return false;
  }
}

export async function createCapperReview(
  handle: string,
  reviewData: CreateCapperReview,
  token: string
): Promise<CapperReviewModel | null> {
  const result = await mlClient.post<CapperReviewModel>(
    `/cappers/${handle}/review`,
    reviewData,
    token
  );

  if (!result.ok) {
    console.error("Failed to create capper review:", result.message);
    return null;
  }

  return result.data || null;
}

export async function getCapperReviews(
  bettorId: number
): Promise<CapperReviewModel[]> {
  const result = await mlClient.get<CapperReviewModel[]>(
    `/cappers/reviews/${bettorId}`
  );

  if (!result.ok) {
    console.error("Failed to fetch capper reviews:", result.message);
    return [];
  }

  return result.data || [];
}
export interface CapperProfileFormData {
  handle: string;
  name?: string;
  tagline?: string;
  about?: string;
  avatar_img_key?: string;
  cover_img_key?: string;
  big_winners?: string[];
  faq?: Array<{ question: string; answer: string }>;
  video_embed_url?: string;
  instagram_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  facebook_url?: string;
  x_url?: string;
}

export interface CapperReviewFormData {
  rating: number;
  comment: string;
}

export function validateCapperProfileData(data: CapperProfileFormData): {
  isValid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};

  try {
    updateCapperProfileSchema.parse({
      handle: data.handle?.replace("@", ""),
      name: data.name,
      tagline: data.tagline,
      about: data.about,
      avatar_img_key: data.avatar_img_key,
      cover_img_key: data.cover_img_key,
      big_winners: data.big_winners,
      faq: data.faq,
      video_embed_url: data.video_embed_url,
      instagram_url: data.instagram_url,
      youtube_url: data.youtube_url,
      tiktok_url: data.tiktok_url,
      facebook_url: data.facebook_url,
      x_url: data.x_url,
    });
  } catch (error: any) {
    if (error.errors) {
      error.errors.forEach((err: any) => {
        const field = err.path.join("_");
        errors[field] = err.message;
      });
    } else {
      errors.general = "Invalid data provided";
    }
  }

  if (data.video_embed_url && data.video_embed_url.trim()) {
    if (!isValidVideoUrl(data.video_embed_url.trim())) {
      errors.video_embed_url = "Please enter a valid YouTube or Vimeo URL";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function validateCapperReviewData(data: CapperReviewFormData): {
  isValid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};

  try {
    createCapperReviewSchema.parse({
      rating: data.rating,
      comment: data.comment,
    });
  } catch (error: any) {
    if (error.errors) {
      error.errors.forEach((err: any) => {
        const field = err.path.join("_");
        errors[field] = err.message;
      });
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}
